import countriesMeta from "../data/countries.json";

const normalizeCountryName = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const metaByCode = new Map(
  countriesMeta.map((country) => [String(country.code).toUpperCase(), country]),
);

const metaByName = new Map(
  countriesMeta.map((country) => [normalizeCountryName(country.name), country]),
);

export const findCountryMetaByCode = (code) =>
  code ? metaByCode.get(String(code).toUpperCase()) ?? null : null;

export const findCountryMetaByName = (name) =>
  name ? metaByName.get(normalizeCountryName(name)) ?? null : null;

export const PHONE_COUNTRY_OPTIONS = countriesMeta.map((country) => ({
  code: country.code,
  name: country.name,
  dial: country.dial,
  flagSrc: country.flagSrc,
}));

const getBackendCountryName = (country) =>
  country?.description || country?.name || "";

export function toCountryOption(country) {
  if (!country) return null;

  const description = getBackendCountryName(country);
  const meta =
    findCountryMetaByCode(country.code) ?? findCountryMetaByName(description);

  return {
    id: country.id ?? null,
    code: meta?.code ?? (country.code ? String(country.code).toUpperCase() : ""),
    name: meta?.name ?? description,
    description,
    dial: meta?.dial ?? country.dial ?? "",
    flagSrc: meta?.flagSrc ?? country.flagSrc ?? "",
  };
}

export function buildCountryOptions(backendCountries = []) {
  const seenCodes = new Set();

  return (backendCountries || [])
    .map(toCountryOption)
    .filter((option) => option && (option.id != null || option.code))
    .sort((left, right) => (left.id ?? 0) - (right.id ?? 0))
    .filter((option) => {
      if (!option.code) return true;
      if (seenCodes.has(option.code)) return false;
      seenCodes.add(option.code);
      return true;
    })
    .sort((left, right) => left.name.localeCompare(right.name, "es"));
}

export function findCountryOption(options, value) {
  if (!value || !options?.length) return null;

  if (typeof value === "string") {
    const code = value.toUpperCase();
    const byCode = options.find((option) => option.code === code);
    if (byCode) return byCode;

    const name = normalizeCountryName(value);
    return (
      options.find(
        (option) =>
          normalizeCountryName(option.description) === name ||
          normalizeCountryName(option.name) === name,
      ) ?? null
    );
  }

  if (value.id != null) {
    const byId = options.find((option) => option.id === value.id);
    if (byId) return byId;
  }

  if (value.code) {
    const code = String(value.code).toUpperCase();
    const byCode = options.find((option) => option.code === code);
    if (byCode) return byCode;
  }

  const name = normalizeCountryName(getBackendCountryName(value));
  if (!name) return null;

  return (
    options.find(
      (option) =>
        normalizeCountryName(option.description) === name ||
        normalizeCountryName(option.name) === name,
    ) ?? null
  );
}
